import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { startLogin, login, getUserInfo } from 'service/AuthService'
import { getMe } from 'service/AdminPanelService'
import { beginAuthSession, currentAuthSession } from 'http/authSession'
import { setUser, removeUser } from 'store/slices/UserSlice'
import { setUser as setAuthUser, setPinVerified, clearUser } from 'store/slices/AuthSlice'
import eye from 'assets/icons/eye.png'
import hidden from 'assets/icons/hidden.png'
import styles from './LoginForm.module.scss'

const EXPIRED_CODES = new Set(['invalid_challenge', 'challenge_expired', 'pin_state_changed'])
const STORAGE_KEYS = ['token', 'refresh', 'login_roles', 'login_primary_role', 'user', 'auth_session']

function PinField({ id, label, value, onChange, setup, confirm, disabled, error, inputRef, t }) {
  const [visible, setVisible] = useState(false)
  const show = confirm ? t('auth.show_pin_confirm', 'Показать повтор ПИН') : t('auth.show_pin', 'Показать ПИН')
  const hide = confirm ? t('auth.hide_pin_confirm', 'Скрыть повтор ПИН') : t('auth.hide_pin', 'Скрыть ПИН')
  return <>
    <label className={styles.login__title__label} htmlFor={id}>{label}</label>
    <div className={styles.input__wrap}>
      <input ref={inputRef} id={id} name={id} value={value} type={visible ? 'text' : 'password'} inputMode="numeric"
        autoComplete={setup ? 'new-password' : 'current-password'} maxLength={12} required disabled={disabled}
        onChange={event => onChange(event.target.value.replace(/\D/g, ''))}
        className={`${styles.login__input} ${styles.input__withIcon}`} aria-invalid={!!error}
        aria-describedby={error ? 'login-error login-help' : 'login-help'} />
      <button type="button" disabled={disabled} className={styles.show__password} onClick={() => setVisible(!visible)}
        aria-label={visible ? hide : show} aria-pressed={visible}><img src={visible ? hidden : eye} alt="" /></button>
    </div>
  </>
}

function LoginForm() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [inn, setInn] = useState('')
  const [step, setStep] = useState('inn')
  const [challenge, setChallenge] = useState('')
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const requestVersion = useRef(0)
  const submitting = useRef(false)
  const innInput = useRef(null)
  const pinInput = useRef(null)
  const isSetup = step === 'setup'

  useEffect(() => { (step === 'inn' ? innInput.current : pinInput.current)?.focus() }, [step])
  useEffect(() => () => { requestVersion.current += 1 }, [])

  const resetPins = () => { setPin(''); setPinConfirm('') }
  const returnToInn = () => {
    if (submitting.current) return
    requestVersion.current += 1
    resetPins(); setChallenge(''); setError(''); setStep('inn')
  }
  const clearSession = () => {
    STORAGE_KEYS.forEach(key => localStorage.removeItem(key))
    dispatch(clearUser()); dispatch(removeUser())
  }
  const errorMessage = (err, fallback) => {
    const data = err?.response?.data || {}
    if (data.code === 'account_locked') return t('auth.pin_locked', 'Аккаунт заблокирован. Дождитесь окончания блокировки или обратитесь к администратору.')
    if (EXPIRED_CODES.has(data.code)) return t('auth.login_step_expired', 'Проверка ИНН устарела. Нажмите «Продолжить» ещё раз.')
    if (err?.response?.status === 429) return t('auth.login_rate_limited', 'Слишком много попыток. Подождите немного и попробуйте снова.')
    const detail = data.detail || data.pin_confirm || data.pin || data.inn
    const message = Array.isArray(detail) ? detail.join(' ') : detail
    return typeof message === 'string' ? message : fallback
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (submitting.current) return
    setError('')
    if (!inn.trim()) { setError(t('auth.enter_inn', 'Введите ИНН.')); return }
    if (step !== 'inn' && !/^[0-9]{4,12}$/.test(pin)) { setError(t('auth.pin_format', 'ПИН должен содержать от 4 до 12 цифр.')); return }
    if (isSetup && pin !== pinConfirm) { setError(t('auth.pin_mismatch', 'ПИН-коды не совпадают. Повторите новый ПИН.')); return }
    submitting.current = true
    const version = ++requestVersion.current
    const current = () => version === requestVersion.current
    setLoading(true)
    let authenticating = false
    let tokenIssued = false
    try {
      if (step === 'inn') {
        const { data } = await startLogin(inn.trim())
        if (!current()) return
        if (!['login', 'setup'].includes(data?.mode) || typeof data.challenge !== 'string' || !data.challenge) throw new Error('invalid_login_step')
        resetPins(); setChallenge(data.challenge); setStep(data.mode)
        return
      }
      authenticating = true
      const { data } = await login(inn.trim(), pin, { challenge, ...(isSetup ? { pin_confirm: pinConfirm } : {}) })
      if (!current()) return
      if (!data?.access || !data.refresh || !data.user_id || data.pin_verified !== true) throw new Error('invalid_login_response')
      clearSession()
      localStorage.setItem('token', data.access); localStorage.setItem('refresh', data.refresh)
      const session = beginAuthSession()
      tokenIssued = true
      const [profileResult, meResult] = await Promise.allSettled([getUserInfo(data.user_id), getMe()])
      if (!current()) {
        if (currentAuthSession() === session) STORAGE_KEYS.forEach(key => localStorage.removeItem(key))
        return
      }
      if (meResult.status !== 'fulfilled') throw meResult.reason
      const me = meResult.value?.data
      if (!me || me.id !== data.user_id) throw new Error('invalid_profile_response')
      if (me.is_locked || me.employee_is_locked || me.is_active === false) {
        const locked = new Error('account_locked'); locked.response = { data: { code: 'account_locked' } }; throw locked
      }
      const profile = profileResult.status === 'fulfilled' ? profileResult.value?.data || {} : {}
      const userData = {
        ...profile, user: data.user_id, id: me.employee_id ?? profile.id,
        first_name: profile.first_name || me.first_name, surname: profile.surname || me.last_name,
        email: profile.email || me.email, token: data.access,
      }
      delete userData.pin
      dispatch(setUser(userData)); dispatch(setAuthUser(me)); dispatch(setPinVerified(true))
      localStorage.setItem('user', JSON.stringify(userData))
      localStorage.setItem('login_roles', JSON.stringify(Array.isArray(me.roles) ? me.roles : []))
      if (me.primary_role?.code || typeof me.primary_role === 'string') localStorage.setItem('login_primary_role', me.primary_role?.code || me.primary_role)
      navigate('/post-login/', { replace: true })
    } catch (err) {
      if (!current()) return
      if (authenticating) clearSession()
      if (tokenIssued || EXPIRED_CODES.has(err?.response?.data?.code)) { resetPins(); setChallenge(''); setStep('inn') }
      else if (authenticating) { resetPins(); pinInput.current?.focus() }
      setError(errorMessage(err, tokenIssued
        ? t('auth.profile_load_failed', 'Не удалось загрузить аккаунт. Повторите вход, начиная с ИНН.')
        : step === 'inn'
        ? t('auth.inn_check_failed', 'Не удалось проверить ИНН. Попробуйте ещё раз.')
        : t('auth.pin_login_failed', 'Не удалось войти. Проверьте ИНН и ПИН.')))
    } finally { if (current()) { submitting.current = false; setLoading(false) } }
  }

  const title = step === 'inn' ? t('auth.pin_login_title', 'Вход в систему')
    : isSetup ? t('auth.setup_pin_title', 'Создайте ПИН-код') : t('auth.enter_pin_title', 'Введите ПИН-код')
  const subtitle = step === 'inn' ? t('auth.inn_step_subtitle', 'Сначала введите свой ИНН. Мы проверим, настроен ли вход в ваш аккаунт.')
    : isSetup ? t('auth.setup_pin_subtitle', 'ПИН ещё не задан. Придумайте его и повторите для подтверждения.')
      : t('auth.enter_pin_subtitle', 'Для этого аккаунта ПИН уже задан. Введите его, чтобы войти.')

  return <div className={styles.card}>
    <span className={styles.eyebrow}>{t('auth.personal_account', 'Личный кабинет')}</span>
    <ol className={styles.steps} aria-label={t('auth.login_steps', 'Этапы входа')}>
      <li className={step === 'inn' ? styles.activeStep : styles.completedStep} aria-current={step === 'inn' ? 'step' : undefined}>
        <span aria-hidden="true">{step === 'inn' ? '1' : '✓'}</span>{t('auth.inn', 'ИНН')}
      </li>
      <li className={step !== 'inn' ? styles.activeStep : ''} aria-current={step !== 'inn' ? 'step' : undefined}>
        <span aria-hidden="true">2</span>{t('auth.pin_label', 'ПИН')}
      </li>
    </ol>
    <h2 className={styles.login__title}>{title}</h2>
    <p className={styles.login__untitle}>{subtitle}</p>
    <form onSubmit={handleSubmit} noValidate aria-busy={loading} aria-label={title}>
      {step === 'inn' ? <>
        <label className={styles.login__title__label} htmlFor="login-inn">{t('auth.inn', 'ИНН')}</label>
        <input ref={innInput} id="login-inn" name="username" value={inn} type="text" inputMode="numeric"
          autoComplete="username" maxLength={32} required disabled={loading}
          onChange={event => { setInn(event.target.value.replace(/\D/g, '')); setError('') }}
          className={styles.login__input} aria-invalid={!!error} aria-describedby={error ? 'login-error' : 'login-help'} />
        <p id="login-help" className={styles.helper}>{t('auth.inn_step_help', 'Используйте ИНН, указанный в вашей учётной записи.')}</p>
      </> : <>
        <div className={styles.identifiedAccount}><div><span>{t('auth.inn', 'ИНН')}</span><strong>{inn}</strong></div>
          <button type="button" disabled={loading} onClick={returnToInn}>{t('auth.change_inn', 'Изменить ИНН')}</button>
        </div>
        <PinField key={step} id="login-pin" label={isSetup ? t('auth.new_pin_label', 'Новый ПИН') : t('auth.pin_label', 'ПИН')}
          value={pin} onChange={value => { setPin(value); setError('') }} setup={isSetup} disabled={loading} error={error} inputRef={pinInput} t={t} />
        {isSetup && <PinField id="login-pin-confirm" label={t('auth.repeat_pin_label', 'Повторите новый ПИН')}
          value={pinConfirm} onChange={value => { setPinConfirm(value); setError('') }} setup confirm disabled={loading} error={error} t={t} />}
        <p id="login-help" className={styles.helper}>{isSetup ? t('auth.setup_pin_help', 'От 4 до 12 цифр. Этот ПИН понадобится для следующих входов.')
          : t('auth.enter_pin_help', 'Введите ваш действующий ПИН-код.')}</p>
      </>}
      {error && <div id="login-error" className={styles.error} role="alert">{error}</div>}
      <button className={styles.submit} type="submit" disabled={loading}>
        {loading ? (step === 'inn' ? t('auth.checking_inn', 'Проверяем…') : t('auth.signing_in', 'Входим…'))
          : step === 'inn' ? t('auth.continue_login', 'Продолжить')
            : isSetup ? t('auth.setup_pin_and_login', 'Сохранить ПИН и войти') : t('auth.signin', 'Войти')}
        {!loading && <span aria-hidden="true">→</span>}
      </button>
    </form>
    <p className={styles.support}>{t('auth.pin_support', 'Забыли ПИН или не можете войти? Обратитесь в отдел кадров или к администратору для восстановления доступа.')}</p>
  </div>
}

export default LoginForm
